import React, { useState, ChangeEvent } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import FormInput from "../../questionnaire/components/FormInput";
import FormSelect from "../../questionnaire/components/FormSelect";
import ProgressBar from "../../questionnaire/components/ProgressBar";
import AppButton from "../../questionnaire/components/AppButton";
import { useAppointmentSteps, useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useDemographics } from "../../questionnaire/contexts/DemographicsContext";
import { useMasterData } from "../../questionnaire/contexts/MasterDataContext";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import {
  getDefaultPharmacy,
  searchPharmacies,
  updateDefaultPharmacy,
  DefaultPharmacy,
  PharmacySearchItem,
} from "../../questionnaire/api/athena";
import { usStates } from "../../questionnaire/questionnaire_constants";
import { Alert } from "../../components/shared/Alert";
import axiosInstance from "../../utils/axiosConfig";

const insurancePlaceholder = { label: "Select Provider", value: "" };


const CareAndInsurance: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { demographics, loading, error, updateDemographics } = useDemographics();
  const { data: masterData } = useMasterData();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError } = useWorkflow();
 
  const steps = useAppointmentSteps("care-and-insurance");
  const emergencyPath = useAppointmentStepPath("emergency-and-billing");
  const medicalPath = useAppointmentStepPath("medical-history");

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [hasPrimaryInsurance, setHasPrimaryInsurance] = useState<"yes" | "no">("yes");
  const [wantsToUpdateInsurance, setWantsToUpdateInsurance] = useState(false);
  const [insuranceCardFile, setInsuranceCardFile] = useState<string | null>(null);

  const [pharmacySearchLoading, setPharmacySearchLoading] = useState(false);
  const [pharmacySaveLoading, setPharmacySaveLoading] = useState(false);
  const [pharmacyError, setPharmacyError] = useState<string | null>(null);
  const [defaultPharmacy, setDefaultPharmacy] = useState<DefaultPharmacy | null>(null);
  const [pharmacySearchName, setPharmacySearchName] = useState("");
  const [pharmacySearchZip, setPharmacySearchZip] = useState("");
  const [pharmacySearchCity, setPharmacySearchCity] = useState("");
  const [pharmacySearchState, setPharmacySearchState] = useState("");
  const [pharmacyResults, setPharmacyResults] = useState<PharmacySearchItem[]>([]);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | number | null>(null);
  const [showPharmacySearch, setShowPharmacySearch] = useState(false);

  // Insurance update handlers
  const handleInsuranceToggle = (value: "yes" | "no") => {
    setHasPrimaryInsurance(value);
  };

  const handleInsuranceCardUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setInsuranceCardFile(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setInsuranceCardFile(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const primaryProviderIdValue = formData.get("primary_care_provider") as string;

    const payload = {
      primaryProviderId: primaryProviderIdValue
        ? parseInt(primaryProviderIdValue, 10)
        : demographics?.primaryProvider?.providerId,
    };

    setSubmitting(true);
    setLocalError(null);

    // When user already has insurance and chose not to update, skip insurance API calls
    const shouldUpdateInsurance = !primaryInsurance || wantsToUpdateInsurance;

    if (hasPrimaryInsurance === "yes" && shouldUpdateInsurance) {
      const insuranceFile = (formData.get("insurance_card_upload") as File) || null;
      const insurancePackageId = formData.get("insurance_provider") as string;
      const insuranceIdNumber = formData.get("insurance_member_id") as string;

      // Validate required insurance fields
      if (!insuranceFile || insuranceFile.size === 0) {
        setSubmitting(false);
        setLocalError("Please upload your insurance card photo");
        return;
      }

      if (!insurancePackageId) {
        setSubmitting(false);
        setLocalError("Please select your insurance provider");
        return;
      }

      if (!insuranceIdNumber) {
        setSubmitting(false);
        setLocalError("Please enter your insurance member ID");
        return;
      }

      if (insuranceFile && insuranceFile.size > 0) {
        const patientId = demographics?.patientId;

        console.log("Insurance card upload:", { 
          file: insuranceFile?.name, 
          type: insuranceFile?.type, 
          size: insuranceFile?.size,
          packageId: insurancePackageId,
          memberId: insuranceIdNumber 
        });

        if (!patientId) {
          setSubmitting(false);
          throw new Error("Missing patientId for insurance update with image");
        }

        const formDataUpload = new FormData();

        const requestPayload = {
          insurancePackageId: insurancePackageId ? parseInt(insurancePackageId, 10) : undefined,
          insuranceIdNumber: insuranceIdNumber || undefined,
          sequencenumber: 1,
        };
        const requestBlob = new Blob([JSON.stringify(requestPayload)], { type: 'application/json' });
        formDataUpload.append("request", requestBlob);
        formDataUpload.append("insuranceImage", insuranceFile);

        try {
          await axiosInstance.post(
            `/api/athena/digital-checkin/insurance/${encodeURIComponent(String(patientId))}/appointment/${encodeURIComponent(String(appointmentId))}`,
            formDataUpload,
            {
              headers: { 
                client: 'axis', 
                domain: 'axis',
                // Don't set Content-Type - axios will set it automatically with boundary for FormData
              }
            }
          );
        } catch (uploadError: any) {
          console.error("Failed to upload insurance card", uploadError);
          const errorMsg = uploadError?.response?.data?.error || 
                          uploadError?.response?.data?.message || 
                          uploadError?.message || 
                          "Failed to upload insurance card";
          setLocalError(errorMsg);
          setSubmitting(false);
          throw new Error(errorMsg);
        }
      }
    }

    if (hasPrimaryInsurance === "no" && shouldUpdateInsurance) {
      const patientId = demographics?.patientId;
      if (!patientId || !appointmentId) {
        setSubmitting(false);
        setLocalError("Missing patient or appointment information.");
        return;
      }
      const noInsurancePayload = { insurancePackageId: 0, sequencenumber: 1 };
      const formDataNoInsurance = new FormData();
      const requestBlob = new Blob([JSON.stringify(noInsurancePayload)], { type: "application/json" });
      formDataNoInsurance.append("request", requestBlob);
      try {
        await axiosInstance.post(
          `/api/athena/digital-checkin/insurance/${encodeURIComponent(String(patientId))}/appointment/${encodeURIComponent(String(appointmentId))}`,
          formDataNoInsurance,
          {
            headers: {
              client: "axis",
              domain: "axis",
            },
          }
        );
      } catch (apiError: any) {
        console.error("Failed to update insurance (no insurance)", apiError);
        const errorMsg =
          apiError?.response?.data?.error ||
          apiError?.response?.data?.message ||
          apiError?.message ||
          "Failed to update insurance information.";
        setLocalError(errorMsg);
        setSubmitting(false);
        return;
      }
    }

    try {
      await updateDemographics(payload);
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(medicalPath);
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unable to save your information. Please try again.";
      setLocalError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndExit = async () => {
    setSubmitting(true);
    setLocalError(null);
    try {
      await updateDemographics({});
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(medicalPath.replace("medical-history", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const primaryProvider = demographics?.primaryProvider;
  const primaryInsurance =
    demographics?.insurances && Array.isArray(demographics.insurances)
      ? demographics.insurances[0]
      : undefined;

  const insurancePackageDefault =
    primaryInsurance && primaryInsurance.insurancePackageId != null
      ? String(primaryInsurance.insurancePackageId)
      : "";

  const insuranceMemberIdDefault =
    primaryInsurance && primaryInsurance.memberId != null
      ? String(primaryInsurance.memberId)
      : "";

  const insuranceKey = `${insurancePackageDefault}-${insuranceMemberIdDefault}`;

  React.useEffect(() => {
    const patientId = demographics?.patientId;
    const departmentId = demographics?.departmentId;

    if (!patientId || !departmentId) {
      return;
    }

    let cancelled = false;

    const loadDefaultPharmacy = async () => {
      setPharmacyError(null);
      try {
        const data = await getDefaultPharmacy(patientId, departmentId);
        if (cancelled) return;
        if (data) {
          setDefaultPharmacy(data);
          if (data.clinicalproviderid != null) {
            setSelectedPharmacyId(data.clinicalproviderid);
          }
        } else {
          setDefaultPharmacy(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load default pharmacy", err);
          setPharmacyError("Unable to load your preferred pharmacy.");
        }
      } finally {
        if (!cancelled) {
          // Initial load doesn't need a loading state for buttons
        }
      }
    };

    loadDefaultPharmacy();

    return () => {
      cancelled = true;
    };
  }, [demographics?.patientId, demographics?.departmentId]);

  const handlePharmacySearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pharmacySearchName.trim()) {
      setPharmacyError("Please enter a pharmacy name to search.");
      return;
    }

    const hasZip = pharmacySearchZip.trim().length > 0;
    const hasCity = pharmacySearchCity.trim().length > 0;
    const hasState = pharmacySearchState.trim().length > 0;

    if (!hasZip && (!hasCity || !hasState)) {
      setPharmacyError("Please enter either ZIP code or both city and state to search.");
      return;
    }

      setPharmacySearchLoading(true);
      setPharmacyError(null);
      try {
        const result = await searchPharmacies({
          name: pharmacySearchName.trim(),
          zip: hasZip ? pharmacySearchZip.trim() : undefined,
        city: hasCity ? pharmacySearchCity.trim() : undefined,
        state: hasState ? pharmacySearchState.trim() : undefined,
      });
      setPharmacyResults(result.clinicalproviders || []);
      if (!result.clinicalproviders || result.clinicalproviders.length === 0) {
        setPharmacyError("No pharmacies found. Try a different search.");
      }
    } catch (err) {
      console.error("Failed to search pharmacies", err);
      setPharmacyError("Unable to search pharmacies. Please try again.");
    } finally {
      setPharmacySearchLoading(false);
    }
  };

  const handleSelectPharmacy = (item: PharmacySearchItem) => {
    if (item.clinicalproviderid == null) return;
    setSelectedPharmacyId(item.clinicalproviderid);
  };

  const handleSavePharmacy = async () => {
    const patientId = demographics?.patientId;
    const departmentId = demographics?.departmentId;

    if (!patientId || !departmentId || selectedPharmacyId == null) {
      setPharmacyError("Missing patient, department, or pharmacy selection.");
      return;
    }

    setPharmacySaveLoading(true);
    setPharmacyError(null);
    try {
      await updateDefaultPharmacy(patientId, {
        departmentId,
        clinicalProviderId: selectedPharmacyId,
      });
      // Optimistically update local state so UI reflects selection
      const chosen = pharmacyResults.find(
        (p) => String(p.clinicalproviderid) === String(selectedPharmacyId)
      );
      if (chosen) {
        setDefaultPharmacy({
          clinicalprovidername: chosen.name,
          city: chosen.city,
          state: chosen.state,
          zip: chosen.zip,
          clinicalproviderid:
            typeof chosen.clinicalproviderid === "string"
              ? parseInt(chosen.clinicalproviderid, 10)
              : chosen.clinicalproviderid,
          phonenumber: chosen.phone,
          address1: chosen.address,
        });
      }
      setShowPharmacySearch(false);
    } catch (err) {
      console.error("Failed to update default pharmacy", err);
      setPharmacyError("Unable to save your preferred pharmacy. Please try again.");
    } finally {
      setPharmacySaveLoading(false);
    }
  };

  return (

    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        <h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">
          Care & Insurance Details
        </h1>
        <div className="text-xl text-[#1C1C1C] text-center mb-6">Help us coordinate your care</div>
        {loading && (
          <div className="text-sm text-[#5D6A85] mb-2">Loading your information…</div>
        )}
        {(error || localError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert 
              type="error" 
              onClose={() => {
                setLocalError(null);
              }}
            >
              {error || localError}
            </Alert>
          </div>
        )}
        <ProgressBar steps={steps} />
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <section className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="text-base font-semibold text-[#1C1C1C]">Primary Care Provider</div>
                <div className="text-sm text-[#5D6A85]">Select your primary mental health provider</div>
              </div>
              <FormSelect
                label=""
                name="primary_care_provider"
                searchable
                options={[
                  { label: "Select Provider", value: "" },
                  ...(masterData?.providers?.map((p) => ({
                    label: p.specialty ? `${p.name} | ${p.specialty}` : p.name,
                    value: p.code,
                  })) || []),
                ]}
                defaultValue={
                  primaryProvider?.providerId != null
                    ? String(primaryProvider.providerId)
                    : ""
                }
              />
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="text-base font-semibold text-[#1C1C1C]">Preferred Pharmacy</div>
              </div>

                {(pharmacySearchLoading || pharmacySaveLoading) && (
                  <div className="text-sm text-[#5D6A85] mb-1">Loading pharmacy information…</div>
                )}
                {pharmacyError && (
                  <div className="mb-1">
                    <Alert 
                      type="error" 
                      onClose={() => {
                        setPharmacyError(null);
                      }}
                    >
                      {pharmacyError}
                    </Alert>
                  </div>
                )}

                {defaultPharmacy && !showPharmacySearch && (
                  <div className="border border-[#DCE1E8] rounded-md p-3 text-sm text-[#1C1C1C] bg-[#F7F8FD] flex flex-col gap-1">
                    <div className="font-semibold">Current pharmacy on file</div>
                    <div>{defaultPharmacy.clinicalprovidername}</div>
                    <div>
                      {defaultPharmacy.address1}
                      {defaultPharmacy.city && defaultPharmacy.state && (
                        <>
                          {", "}
                          {defaultPharmacy.city}, {defaultPharmacy.state} {defaultPharmacy.zip}
                        </>
                      )}
                    </div>
                    {defaultPharmacy.phonenumber && (
                      <div>Phone: {defaultPharmacy.phonenumber}</div>
                    )}
                    <button
                      type="button"
                      className="mt-2 text-sm text-[#01545D] underline self-start"
                      onClick={() => setShowPharmacySearch(true)}
                    >
                      Change pharmacy
                    </button>
                  </div>
                )}

                {(!defaultPharmacy || showPharmacySearch) && (
                  <div className="flex flex-col gap-3">
                    <div className="text-sm text-[#5D6A85] mb-2">
                      Enter name + ZIP or name + city + state to search
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-[#1C1C1C]">
                          Pharmacy Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="pharmacy_name"
                          placeholder="Pharmacy name"
                          value={pharmacySearchName}
                          onChange={(e) => setPharmacySearchName(e.target.value)}
                          className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          name="pharmacy_zip"
                          placeholder="ZIP code"
                          value={pharmacySearchZip}
                          onChange={(e) => {
                            setPharmacySearchZip(e.target.value);
                            // Clear city/state when zip is entered
                            if (e.target.value.trim()) {
                              setPharmacySearchCity("");
                              setPharmacySearchState("");
                            }
                          }}
                          className="w-40 rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                        />
                        <div className="text-sm font-medium text-[#5D6A85]">OR</div>
                        <input
                          type="text"
                          name="pharmacy_city"
                          placeholder="City"
                          value={pharmacySearchCity}
                          onChange={(e) => {
                            setPharmacySearchCity(e.target.value);
                            // Clear zip when city/state is entered
                            if (e.target.value.trim() || pharmacySearchState.trim()) {
                              setPharmacySearchZip("");
                            }
                          }}
                          className="flex-1 rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                        />
                        <div className="flex-1">
                          <FormSelect
                            label=""
                            name="pharmacy_state"
                            options={usStates}
                            value={pharmacySearchState}
                            onChange={(e) => {
                              setPharmacySearchState(e.target.value);
                              // Clear zip when city/state is entered
                              if (e.target.value.trim() || pharmacySearchCity.trim()) {
                                setPharmacySearchZip("");
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-2">
                      <button
                        type="button"
                        onClick={handlePharmacySearchSubmit}
                        className="px-4 py-2 bg-[#01545D] text-white rounded-md text-sm"
                        disabled={pharmacySearchLoading}
                      >
                        {pharmacySearchLoading ? "Searching…" : "Search"}
                      </button>
                      {defaultPharmacy && (
                        <button
                          type="button"
                          className="text-sm text-[#5D6A85] underline"
                          onClick={() => setShowPharmacySearch(false)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>

                    {pharmacyResults.length > 0 && (
                      <div className="border border-[#DCE1E8] rounded-md max-h-56 overflow-y-auto divide-y divide-[#E2E8F0]">
                        {pharmacyResults.map((pharmacy) => {
                          const id = pharmacy.clinicalproviderid;
                          const isSelected =
                            id != null && String(id) === String(selectedPharmacyId);
                          return (
                            <button
                              key={`${pharmacy.ncpdpid || id}`}
                              type="button"
                              onClick={() => handleSelectPharmacy(pharmacy)}
                              className={
                                "w-full text-left p-3 text-sm flex flex-col gap-0.5 " +
                                (isSelected ? "bg-[#E0F2F3]" : "bg-white hover:bg-[#F7F8FD]")
                              }
                            >
                              <div className="font-semibold">{pharmacy.name}</div>
                              <div>
                                {pharmacy.address}
                                {pharmacy.city && pharmacy.state && (
                                  <>
                                    {", "}
                                    {pharmacy.city}, {pharmacy.state} {pharmacy.zip}
                                  </>
                                )}
                              </div>
                              {pharmacy.phone && <div>Phone: {pharmacy.phone}</div>}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedPharmacyId != null && (
                      <button
                        type="button"
                        onClick={handleSavePharmacy}
                        className="self-start px-4 py-2 bg-[#01545D] text-white rounded-md text-sm"
                        disabled={pharmacySaveLoading}
                      >
                        {pharmacySaveLoading ? "Saving…" : "Save preferred pharmacy"}
                      </button>
                    )}
                  </div>
                )}
            </div>
          </section>

          {/* Insurance Information - Read-only display if available */}
          {primaryInsurance && (
            <section className="flex flex-col gap-6">
              <div className="text-xl font-semibold text-[#01545D]">Insurance Information</div>
              <div className="border border-[#DCE1E8] rounded-md p-4 bg-[#F7F8FD] flex flex-col gap-3">
                {(insurancePackageDefault || primaryInsurance?.insurancePackageName) && (
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-semibold text-[#5D6A85]">Insurance Provider</div>
                    <div className="text-base text-[#1C1C1C]">
                      {primaryInsurance?.insurancePackageName || insurancePackageDefault}
                    </div>
                  </div>
                )}
                {insuranceMemberIdDefault && (
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-semibold text-[#5D6A85]">Member ID</div>
                    <div className="text-base text-[#1C1C1C]">{insuranceMemberIdDefault}</div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Insurance update section */}
          <section className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <div className="text-xl font-semibold text-[#01545D]">Insurance Information</div>
              {primaryInsurance && (
                <div className="flex items-center gap-3">
                  <input
                    id="wants_to_update_insurance"
                    type="checkbox"
                    checked={wantsToUpdateInsurance}
                    onChange={(e) => setWantsToUpdateInsurance(e.target.checked)}
                    className="h-4 w-4 rounded border-[#DCE1E8] text-[#01545D] focus:ring-[#01545D]"
                  />
                  <label htmlFor="wants_to_update_insurance" className="text-base font-medium text-[#1C1C1C]">
                    Do you want to update your insurance?
                  </label>
                </div>
              )}
              {(!primaryInsurance || wantsToUpdateInsurance) && (
                <div className="flex flex-col gap-2">
                  <div className="font-semibold text-[#1C1C1C] text-base">
                    Do you have primary insurance? <span className="text-red-500">*</span>
                  </div>
                  <div className="flex gap-4 max-w-md">
                    <button
                      type="button"
                      onClick={() => handleInsuranceToggle("yes")}
                      className={
                        "flex-1 py-3 px-5 rounded-md border text-base font-medium " +
                        (hasPrimaryInsurance === "yes"
                          ? "border-[#01545D] bg-[#01545D] text-white"
                          : "border-[#DCE1E8] bg-white text-[#1C1C1C]")
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInsuranceToggle("no")}
                      className={
                        "flex-1 py-3 px-5 rounded-md border text-base font-medium " +
                        (hasPrimaryInsurance === "no"
                          ? "border-[#01545D] bg-[#01545D] text-white"
                          : "border-[#DCE1E8] bg-white text-[#1C1C1C]")
                      }
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>

            {(!primaryInsurance || wantsToUpdateInsurance) && hasPrimaryInsurance === "no" && (
              <div className="flex items-start gap-3 bg-[#F7F8FD] border border-[rgba(1,84,93,0.3)] rounded-lg px-4 py-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
                  i
                </div>
                <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
                  <div className="font-semibold text-[#01545D]">Self-Pay Options Available</div>
                  <div>
                    No problem. Our team can discuss self-pay rates and payment plans with you during your visit.
                    We're committed to making mental health care accessible.
                  </div>
                </div>
              </div>
            )}

            {(!primaryInsurance || wantsToUpdateInsurance) && hasPrimaryInsurance === "yes" && (
              <div className="flex flex-col gap-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormSelect
                    key={insuranceKey}
                    label="Insurance Provider"
                    name="insurance_provider"
                    options={[
                      insurancePlaceholder,
                      ...(masterData?.insurancePackages?.map((pkg) => ({
                        label: pkg.name,
                        value: pkg.code,
                      })) || []),
                    ]}
                    required
                    defaultValue={insurancePackageDefault}
                  />
                  <FormInput
                    key={`${insuranceKey}-member`}
                    label="Insurance Member ID"
                    name="insurance_member_id"
                    placeholder="Value"
                    required
                    defaultValue={insuranceMemberIdDefault}
                  />
                </div>

                {/* <FormInput
                  label="Insurance Package / Plan Name"
                  name="insurance_plan_name"
                  placeholder="PPO Gold Plan"
                /> */}

                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <div className="text-base font-semibold text-[#1C1C1C]">Insurance Card Photo</div>
                      <div className="text-sm text-[#5D6A85]">
                        Please upload a clear photo of both sides of your insurance card
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="relative w-full sm:w-[360px] h-[150px] flex flex-col items-center justify-center bg-white border-2 border-dashed border-[#A4BDC0] rounded-xl hover:border-[#01545D] transition-colors duration-200 cursor-pointer group p-2">
                        <input
                          id="insurance_card_upload"
                          name="insurance_card_upload"
                          type="file"
                          accept="image/*"
                          onChange={handleInsuranceCardUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        {!insuranceCardFile && (
                          <div className="flex flex-col items-center justify-center pointer-events-none z-0">
                            <svg
                              className="w-8 h-8 text-[#A4BDC0] mb-1"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-[#01545D] font-semibold text-sm">Choose file or drag here</span>
                            <span className="text-[#5D6A85] text-xs mt-1">
                              Supports: JPG, PNG
                            </span>
                          </div>
                        )}
                        {insuranceCardFile && (
                          <div className="flex flex-col items-center justify-center z-0 w-full h-full">
                            <span className="text-xs text-[#5D6A85] mb-1">Preview</span>
                            <img
                              src={insuranceCardFile}
                              alt="Insurance card preview"
                              className="object-contain max-h-full max-w-full rounded border border-[#BEC5D2]"
                            />
                          </div>
                        )}
                      </div>
                      {insuranceCardFile && (
                        <button
                          type="button"
                          onClick={() => setInsuranceCardFile(null)}
                          className="mt-1 text-[#01545D] text-sm underline hover:text-[#0b7981] inline-block self-start"
                        >
                          Remove file
                        </button>
                      )}
                      <div className="flex justify-start items-center text-xs text-[#5D6A85] max-w-[360px]">
                        {/* <button type="button" className="text-[#01545D] underline">
                          Why do we need this?
                        </button> */}
                      </div>
                    </div>
                  </div>
              </div>
            )}
          </section>

          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else {
                  navigate(emergencyPath);
                }
              }}
              disabled={!!workflowError || submitting || loading}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting || loading}
              className="flex-1"
            >
              {submitting ? "Saving..." : "Save & Exit"}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
              {submitting ? "Saving..." : "Continue"}
            </AppButton>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CareAndInsurance;
